import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from '../../atoms/Icon'
import { AnalysisTabButtonMenuProps } from './AnalysisTabButtonMenu.types'
import {
  TabsStickyRoot,
  StickySentinel,
  TabsContainer,
  TabButton,
  TabBadge,
} from './AnalysisTabButtonMenu.styles'

const getScrollParent = (node: HTMLElement | null): Element | null => {
  let current = node?.parentElement
  while (current && current !== document.body) {
    const overflowY = window.getComputedStyle(current).overflowY
    if (
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowY === 'overlay'
    ) {
      return current
    }
    current = current.parentElement
  }
  return null
}

export const AnalysisTabButtonMenu: React.FC<AnalysisTabButtonMenuProps> = ({
  tabs,
  activeTab,
  onTabChange,
  className,
  style,
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [isStuck, setIsStuck] = useState(false)
  const enabledTabs = tabs.filter((t) => !t.disabled)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStuck(!entry.isIntersecting)
      },
      { threshold: 0, root: getScrollParent(sentinel) }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  const handleTabClick = (tab: (typeof tabs)[0]) => {
    if (!tab.disabled) {
      onTabChange(tab.id)
    }
  }

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, tabId: string) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
        return
      }
      e.preventDefault()

      const idx = enabledTabs.findIndex((t) => t.id === tabId)
      if (idx === -1) return

      const next =
        e.key === 'ArrowRight'
          ? (idx + 1) % enabledTabs.length
          : (idx - 1 + enabledTabs.length) % enabledTabs.length
      const nextId = enabledTabs[next].id

      onTabChange(nextId)

      const el = document.querySelector(
        `[data-tab-id="${nextId}"]`
      ) as HTMLElement
      el?.focus()
    },
    [enabledTabs, onTabChange]
  )

  return (
    <TabsStickyRoot>
      <StickySentinel ref={sentinelRef} aria-hidden="true" />
      <TabsContainer
        className={className}
        style={style}
        role="tablist"
        data-stuck={isStuck}
      >
        {tabs.map((tab) => (
          <TabButton
            key={tab.id}
            role="tab"
            tabIndex={activeTab === tab.id ? 0 : -1}
            data-tab-id={tab.id}
            aria-selected={activeTab === tab.id}
            $active={activeTab === tab.id}
            $disabled={tab.disabled}
            $color={tab.color}
            onClick={() => handleTabClick(tab)}
            onKeyDown={(e) => handleKeyDown(e, tab.id)}
          >
            <Icon name={tab.icon as any} size="small" />
            {tab.label}
            {tab.count !== undefined && (
              <TabBadge $color={tab.color} $isEmpty={tab.count === 0}>
                {tab.count}
              </TabBadge>
            )}
          </TabButton>
        ))}
      </TabsContainer>
    </TabsStickyRoot>
  )
}

AnalysisTabButtonMenu.displayName = 'AnalysisTabButtonMenu'
